import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Activity, AlertTriangle, CheckCircle2, Clock, RefreshCw,
  Trash2, ArrowUpRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const PLATFORM_LABEL = {
  facebook: "Facebook", instagram: "Instagram", x: "X",
  tiktok: "TikTok", youtube: "YouTube", threads: "Threads", linkedin: "LinkedIn",
  discord: "Discord", telegram: "Telegram", bluesky: "Bluesky", vimeo: "Vimeo",
  pinterest: "Pinterest",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function SocialHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/social/health");
      setData(r.data);
    } catch {
      setData({ counts: {}, recent_failures: [] });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const retry = async (post_id) => {
    setRetrying(post_id);
    try {
      await api.post(`/social/posts/${post_id}/retry`);
      toast.success("Re-queued — dispatcher will pick it up within 60s");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setRetrying(null); }
  };

  const remove = async (post_id) => {
    if (!await appConfirm("Delete this failed post permanently?")) return;
    try {
      await api.delete(`/social/posts/${post_id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) {
    return (
      <div
        className="rounded-xl border border-line bg-bg-surface p-6 font-mono text-xs text-ink-muted"
        data-testid="social-health-widget-loading"
      >
        LOADING SOCIAL HEALTH...
      </div>
    );
  }

  const counts = data?.counts || {};
  const fails = data?.recent_failures || [];
  const totalFailed = counts.failed || 0;
  const anySignal =
    (counts.scheduled || 0) + totalFailed + (counts.publishing || 0) + (counts.published || 0) > 0;

  return (
    <div
      className={`rounded-xl border bg-bg-surface p-6 ${
        totalFailed > 0 ? "border-rose-500/40" : "border-line"
      }`}
      data-testid="social-health-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className={`h-5 w-5 ${totalFailed > 0 ? "text-rose-400" : "text-emerald-400"}`} />
          <h3 className="font-display text-lg font-bold">Social health</h3>
          {totalFailed > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/20 text-rose-300 text-[10px] font-mono px-2 py-0.5">
              <AlertTriangle className="h-3 w-3" /> {totalFailed} FAILED
            </span>
          )}
        </div>
        <Link
          to="/dashboard/social-connections"
          className="text-xs text-brand-cyan hover:underline inline-flex items-center gap-1"
          data-testid="social-health-manage-link"
        >
          Connections <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>

      {!anySignal ? (
        <div className="text-center py-6 text-sm text-ink-muted" data-testid="social-health-empty">
          No scheduled posts yet. Use the Schedule page to queue your first post.
        </div>
      ) : (
        <>
          {/* Counts strip */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Tile icon={Clock}     label="Scheduled"  value={counts.scheduled || 0} tone="amber" />
            <Tile icon={RefreshCw} label="Publishing" value={counts.publishing || 0} tone="cyan" />
            <Tile icon={CheckCircle2} label="Published" value={counts.published || 0} tone="emerald" />
            <Tile icon={AlertTriangle} label="Failed" value={totalFailed} tone="rose" />
          </div>

          {data?.next_due && (
            <div className="text-xs text-ink-secondary mb-3" data-testid="social-health-next-due">
              Next due: <span className="text-ink-primary font-medium">
                {PLATFORM_LABEL[data.next_due.platform] || data.next_due.platform}
              </span> at <span className="font-mono">{fmtTime(data.next_due.scheduled_for)}</span>
            </div>
          )}

          {fails.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-mono text-ink-muted uppercase tracking-wider mb-2">
                Recent failures
              </div>
              <div className="space-y-2">
                {fails.map((f) => (
                  <div
                    key={f.post_id}
                    data-testid={`social-failure-${f.post_id}`}
                    className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3 flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono uppercase rounded bg-bg-elevated px-1.5 py-0.5">
                          {PLATFORM_LABEL[f.platform] || f.platform}
                        </span>
                        <span className="text-[10px] text-ink-muted font-mono">
                          {fmtTime(f.failed_at)}
                        </span>
                      </div>
                      <div className="text-sm text-ink-primary truncate">{f.content || "(no content)"}</div>
                      <div className="text-xs text-rose-300 mt-1 line-clamp-2">{f.error}</div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() => retry(f.post_id)}
                        disabled={retrying === f.post_id}
                        data-testid={`social-retry-${f.post_id}`}
                        className="text-[11px] inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-2 py-1 hover:bg-brand-amber/20 transition disabled:opacity-50"
                      >
                        {retrying === f.post_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        Retry
                      </button>
                      <button
                        onClick={() => remove(f.post_id)}
                        data-testid={`social-delete-${f.post_id}`}
                        className="text-[11px] inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 hover:text-rose-400 transition"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone }) {
  const toneCls = {
    amber:   "text-brand-amber",
    cyan:    "text-brand-cyan",
    emerald: "text-emerald-400",
    rose:    "text-rose-400",
  }[tone] || "text-ink-primary";
  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-3" data-testid={`social-tile-${label.toLowerCase()}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`h-3.5 w-3.5 ${toneCls}`} />
        <span className="text-[10px] font-mono uppercase tracking-wide text-ink-muted whitespace-nowrap">{label}</span>
      </div>
      <div className={`font-display text-2xl font-bold ${toneCls}`}>{value}</div>
    </div>
  );
}
